import type { TileEnquiryConfig } from '~/modules/enquiry/hooks/use-tile-enquiry';
import type { TileActionInfo } from '~/modules/moodboards/components/TileActionPanel';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';
import type { DetailTile } from './TileCarousel';

export interface DetailTileActions {
    enquiry: TileEnquiryConfig | null;
    share: ShareOptions | null;
    tileInfo: TileActionInfo;
}

/**
 * Enquiry config for a detail tile's action panel. Professional tiles enquire
 * about themselves; product / project tiles enquire their professional and
 * need `professional.id` + `professionalLink` on the tile — without them the
 * panel falls back to More-only (Save / Share). Article tiles are always
 * More-only.
 */
const tileEnquiryFor = (tile: DetailTile): TileEnquiryConfig | null => {
    if (tile.pinType === 'ProfessionalPin') {
        return {
            itemType: 'professional',
            siteTreeID: tile.id,
            professionalId: tile.id,
            professionalName: tile.title,
            professionalLink: tile.link,
            itemTitle: tile.title,
        };
    }
    const itemType =
        tile.pinType === 'ProductPin'
            ? ('product' as const)
            : tile.pinType === 'ProjectPin'
              ? ('project' as const)
              : null;
    if (!itemType || !tile.professional?.id || !tile.professionalLink) {
        return null;
    }
    return {
        itemType,
        siteTreeID: tile.id,
        professionalId: tile.professional.id,
        professionalName: tile.professional.name ?? '',
        professionalLink: tile.professionalLink,
        itemTitle: tile.title,
        itemImage: tile.images?.[0]?.src,
    };
};

/**
 * The action panel config (enquiry / share / mobile sheet header) for a
 * DetailTile-shaped tile, used when the `tile_enquiry_panel` flag is on.
 * Shared by {@link DetailTileCard} and the sections that compose
 * {@link DetailTileMedia} directly.
 */
export const detailTileActionsFor = (tile: DetailTile): DetailTileActions => ({
    enquiry: tile.pinType ? tileEnquiryFor(tile) : null,
    share: tile.link ? { url: tile.link, title: tile.title } : null,
    tileInfo: {
        title: tile.title,
        subtitle: tile.subtitle ?? tile.professional?.name,
        image: tile.logo ?? tile.images?.[0]?.src,
        imageShape: tile.logo ? ('circle' as const) : ('square' as const),
        imageBackground: tile.logo
            ? (tile.logoBackground ?? undefined)
            : undefined,
    },
});
